/**
 * sma-50.js
 *
 * Sample indicator: 50-period Simple Moving Average with slope direction.
 *
 * Visual convention (used on charts and UI badges):
 *   ● Green  — SMA is sloping UP   (bullish momentum)
 *   ● Red    — SMA is sloping DOWN  (bearish momentum)
 *
 * Rubberband trade logic:
 *   LONG  setup → price is BELOW a rising (green) SMA-50
 *                 (price is "stretched" below — expected to snap back up)
 *   SHORT setup → price is ABOVE a falling (red) SMA-50
 *                 (price is "stretched" above — expected to snap back down)
 *
 * Usage:
 *   import { computeSMA50 } from '../indicators/sma-50';
 *   const result = computeSMA50(closePrices);
 *   // result.value      — latest SMA-50 value
 *   // result.slope      — 'up' | 'down' | 'flat'
 *   // result.color      — 'green' | 'red' | 'gray'
 *   // result.allValues  — full SMA array (null for first 49 periods)
 */

import { calculateSMA } from '../utils/technical-indicators';

/**
 * How many candles back to compare for the slope.
 * 3 periods smooths out noise without being too lagging.
 */
const SLOPE_LOOKBACK = 3;

/**
 * Minimum relative change to count as a slope (avoids "flat" being called up/down).
 * 0.05% threshold.
 */
const FLAT_THRESHOLD = 0.0005;

/**
 * Compute the 50-period SMA with slope direction.
 *
 * @param {number[]} closes  Array of closing prices, most recent last.
 * @returns {{
 *   value: number|null,
 *   slope: 'up'|'down'|'flat',
 *   color: 'green'|'red'|'gray',
 *   allValues: (number|null)[]
 * }}
 */
export function computeSMA50(closes) {
  if (!closes || closes.length < 50) {
    return { value: null, slope: 'flat', color: 'gray', allValues: [] };
  }

  const allValues = calculateSMA(closes, 50);
  const last      = allValues[allValues.length - 1];
  const lookback  = allValues[allValues.length - 1 - SLOPE_LOOKBACK];

  if (last === null || lookback === null) {
    return { value: null, slope: 'flat', color: 'gray', allValues };
  }

  const relativeChange = (last - lookback) / lookback;

  let slope, color;
  if (relativeChange > FLAT_THRESHOLD) {
    slope = 'up';
    color = 'green';
  } else if (relativeChange < -FLAT_THRESHOLD) {
    slope = 'down';
    color = 'red';
  } else {
    slope = 'flat';
    color = 'gray';
  }

  return { value: last, slope, color, allValues };
}

/**
 * Classify a coin's rubberband state based on price vs SMA-50.
 *
 * @param {number}   currentPrice
 * @param {number[]} closes         Historical closes (most recent last)
 * @returns {{
 *   state: 'long'|'short'|'none',
 *   sma: number|null,
 *   slope: 'up'|'down'|'flat',
 *   color: 'green'|'red'|'gray',
 *   pctFromSMA: number|null   — negative = price below SMA, positive = price above
 * }}
 */
export function getRubberbandState(currentPrice, closes) {
  const { value: sma, slope, color, allValues } = computeSMA50(closes);

  if (sma === null || currentPrice == null) {
    return { state: 'none', sma: null, slope: 'flat', color: 'gray', pctFromSMA: null };
  }

  const pctFromSMA = ((currentPrice - sma) / sma) * 100;

  let state = 'none';
  if (slope === 'up'   && currentPrice < sma) state = 'long';
  if (slope === 'down' && currentPrice > sma) state = 'short';

  return { state, sma, slope, color, pctFromSMA };
}
